import { useState, useMemo } from "react";
import { PaginationButton } from './buttons/PaginationButton';
import { InvoiceStatusBadge } from './labels/InvoiceStatusBadge';
import { EmptyStateText } from './labels/EmptyStateText';
import { ContentCard, EmptyCard } from './layout/ContentCard';

export interface Invoice {
  id: string;
  amount: number;
  currency: string;
  date: string;
  description: string;
  downloadUrl: string;
  status: string;
}

interface PaymentHistoryProps {
  events: Invoice[];
  pageSize?: number;
}

function formatCurrency(amount: number, currency: string = "USD") {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
  }).format(amount);
}

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function PaymentHistory({
  events,
  pageSize = 20,
}: PaymentHistoryProps) {
  const content = events.length === 0 ? (
    <EmptyCard className="relative">
      <EmptyStateText
        title="No billing history yet"
        subtitle="History will appear here as you use the service"
      />
    </EmptyCard>
  ) : (
    <PaginatedInvoiceTable invoices={events} pageSize={pageSize} />
  );

  return (
    <div className="space-y-4">
      <h2 className="text-[14px] font-medium text-zinc-200">History</h2>
      {content}
    </div>
  );
}

function InvoiceRow({ invoice }: { invoice: Invoice }) {
  return (
    <tr className="transition-colors hover:bg-white/[0.02]">
      <td className="hidden px-4 py-3 font-mono text-[13px] text-zinc-400 sm:table-cell">
        {formatDate(invoice.date)}
      </td>
      <td className="px-4 py-3 font-mono text-[13px] text-zinc-400">
        <div className="flex flex-wrap items-center gap-2">
          <InvoiceStatusBadge status={invoice.status} />
          {invoice.description}
          {invoice.downloadUrl && (
            <a
              href={invoice.downloadUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[11px] text-zinc-500 underline transition-colors hover:text-zinc-300"
            >
              PDF
            </a>
          )}
        </div>
        <div className="mt-1 font-mono text-[10px] text-zinc-600 sm:hidden">
          {formatDate(invoice.date)}
        </div>
      </td>
      <td className="px-4 py-3 text-right font-mono text-[13px] text-zinc-300">
        {formatCurrency(invoice.amount, invoice.currency)}
      </td>
    </tr>
  );
}

interface PaginatedInvoiceTableProps {
  invoices: Invoice[];
  pageSize: number;
}

function PaginatedInvoiceTable(props: PaginatedInvoiceTableProps) {
  const [page, setPage] = useState(1);

  const totalPages = Math.ceil(props.invoices.length / props.pageSize);
  const pageInvoices = useMemo(
    () => props.invoices.slice((page - 1) * props.pageSize, page * props.pageSize),
    [props.invoices, page, props.pageSize],
  );

  return (
    <>
      <InvoiceTable invoices={pageInvoices} />
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="font-mono text-[11px] text-zinc-500">
            Showing {(page - 1) * props.pageSize + 1}-
            {Math.min(page * props.pageSize, props.invoices.length)} of {props.invoices.length}
          </div>
          <div className="flex items-center gap-2">
            <PaginationButton
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              label="Previous"
            />
            <span className="font-mono text-[11px] text-zinc-500">
              Page {page} of {totalPages}
            </span>
            <PaginationButton
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              label="Next"
            />
          </div>
        </div>
      )}
    </>
  );
}

function InvoiceTable({ invoices }: { invoices: Invoice[] }) {
  return (
    <ContentCard>
      <table className="w-full">
        <thead>
          <tr className="border-b border-white/[0.06] bg-white/[0.02]">
            <th className="hidden px-4 py-3 text-left font-mono text-[10px] font-normal uppercase tracking-wider text-zinc-500 sm:table-cell">
              Date
            </th>
            <th className="px-4 py-3 text-left font-mono text-[10px] font-normal uppercase tracking-wider text-zinc-500">
              Description
            </th>
            <th className="px-4 py-3 text-right font-mono text-[10px] font-normal uppercase tracking-wider text-zinc-500">
              Amount
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-white/[0.06]">
          {invoices.map((invoice) => (
            <InvoiceRow key={invoice.id} invoice={invoice} />
          ))}
        </tbody>
      </table>
    </ContentCard>
  );
}
